import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Checkbox, Field, Input, Modal, RadioGroup } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignSummary } from '../../api/client.ts';
import { CHANNEL_LABELS, DELIVERY_LABELS } from './labels.ts';

/** A new draft campaign: name, delivery type and channels. Content and audience come in the composer (BB-97). */
export function CreateCampaignDialog({ environmentId, open, onOpenChange }: { environmentId: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [deliveryType, setDeliveryType] = useState<CampaignSummary['deliveryType']>('scheduled');
  const [channels, setChannels] = useState<CampaignSummary['channels']>(['push']);
  const create = useMutation({
    mutationFn: () => api.createCampaign(environmentId, { name: name.trim(), deliveryType, channels }),
    onSuccess: async (created) => {
      onOpenChange(false);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId] });
      await navigate({ to: '/env/$environmentId/campaigns/$campaignId', params: { environmentId, campaignId: created.id } });
    },
  });
  const toggle = (channel: CampaignSummary['channels'][number], on: boolean) => setChannels((prev) => (on ? [...prev, channel] : prev.filter((c) => c !== channel)));
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Create campaign"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!name.trim() || channels.length === 0} loading={create.isPending} onClick={() => create.mutate()}>
            Create draft
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </Field>
      <RadioGroup label="Delivery" value={deliveryType} onValueChange={(v) => setDeliveryType(v as typeof deliveryType)} options={Object.entries(DELIVERY_LABELS).map(([value, label]) => ({ value, label }))} />
      <fieldset>
        <legend>Channels</legend>
        {(Object.keys(CHANNEL_LABELS) as Array<keyof typeof CHANNEL_LABELS>).map((c) => (
          <Checkbox key={c} label={CHANNEL_LABELS[c]} checked={channels.includes(c)} onCheckedChange={(on) => toggle(c, on)} />
        ))}
      </fieldset>
      {create.isError && <p role="alert">{create.error instanceof ApiError ? create.error.message : 'Something went wrong.'}</p>}
    </Modal>
  );
}
